import { type RouteLocationGeneric } from "vue-router";
import { isAuthenticated } from "@/shared/auth/isAuthenticated.helper";
import { isSuperUser } from "@/shared/auth/isSuperUser.helper.ts";
import { AppIdentifier } from "@/shared/@types/AppIdentifier";
import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import { isDevelopment } from "@/shared/helper/environment.helper";
import { setImpersonationCookie } from "@/shared/helper/impersonationCookie.helper";
import { routes as accountRoutes } from "./account-app/routes";
import { routes as adminControlCenterRoutes } from "./admin-control-center/routes";
import { routes as dashboardRoutes } from "./dashboard/routes";
import { routes as fbsHubRoutes } from "./fbs-hub-app/routes";
import { routes as equivoltRoutes } from "./equivolt-app/routes";
import { routes as gasShopRoutes } from "./gas-shop-app/routes";
import { routes as registrationRoutes } from "./registration-app/routes";
import { routes as thgQuoteRoutes } from "./thg-quote/routes";
import { routes as wccManagementRoutes } from "./wcc-management-app/routes";
import { routes as wscManagementRoutes } from "./wsc-management-app/routes";
import { routes as westfalenIdAccountManagementRoutes } from "./wid-account-management-app/routes";
import { routes as finishRegistrationRoutes } from "./finish-registration-app/routes";

const AccountApp = () => import("./account-app/AccountApp.vue");
const AdminControlCenterApp = () =>
  import("./admin-control-center/AdminControlCenterApp.vue");
const DevLoginApp = () => import("./dev-login/DevLoginApp.vue");
const LoginApp = () => import("./login/LoginApp.vue");
const DashboardApp = () => import("./dashboard/DashboardApp.vue");
const FbsHubApp = () => import("./fbs-hub-app/FbsHubApp.vue");
const EquivoltApp = () => import("./equivolt-app/EquivoltApp.vue");
const GasShopApp = () => import("./gas-shop-app/GasShopApp.vue");
const RegistrationApp = () => import("./registration-app/RegistrationApp.vue");
const ThgQuoteApp = () => import("./thg-quote/ThgQuoteApp.vue");
const WccManagementApp = () =>
  import("./wcc-management-app/WccManagementApp.vue");
const WscManagementApp = () =>
  import("./wsc-management-app/WscManagementApp.vue");
const WestfalenIdAccountManagementApp = () =>
  import("./wid-account-management-app/WidAccountManagementApp.vue");
const FinishRegistrationApp = () =>
  import("./finish-registration-app/FinishRegistrationApp.vue");
const GeneralErrorApp = () => import("./general-error/GeneralErrorApp.vue");
const MaintenanceApp = () => import("./maintenance/MaintenanceApp.vue");

export const routes = [
  {
    path: `/${AppIdentifier.Account}`,
    component: AccountApp,
    children: [...accountRoutes],
  },
  {
    path: `/${AppIdentifier.AdminControlCenter}`,
    component: AdminControlCenterApp,
    children: [...adminControlCenterRoutes],
  },
  {
    path: `/${AppIdentifier.Dashboard}`,
    component: DashboardApp,
    children: [...dashboardRoutes],
  },
  {
    path: `/${AppIdentifier.FbsHub}`,
    component: FbsHubApp,
    children: [...fbsHubRoutes],
  },
  {
    path: `/${AppIdentifier.Equivolt}`,
    component: EquivoltApp,
    children: [...equivoltRoutes],
  },
  {
    path: `/${AppIdentifier.GasShop}`,
    component: GasShopApp,
    children: [...gasShopRoutes],
  },
  {
    path: `/${AppIdentifier.Registration}`,
    component: RegistrationApp,
    children: [...registrationRoutes],
  },
  {
    path: `/${AppIdentifier.ThgQuote}`,
    component: ThgQuoteApp,
    children: [...thgQuoteRoutes],
  },
  {
    path: `/${AppIdentifier.WccManagement}`,
    component: WccManagementApp,
    children: [...wccManagementRoutes],
  },
  {
    path: `/${AppIdentifier.WscManagement}`,
    component: WscManagementApp,
    children: [...wscManagementRoutes],
  },
  {
    path: `/${AppIdentifier.WestfalenIdAccountManagement}`,
    component: WestfalenIdAccountManagementApp,
    children: [...westfalenIdAccountManagementRoutes],
  },
  {
    path: "/login",
    name: AppRouteNames.Login,
    component: LoginApp,
    beforeEnter: async () => {
      const userIsAuthenticated = await isAuthenticated();
      if (userIsAuthenticated) {
        return AppRouteNames.Dashboard;
      }

      if (isDevelopment) {
        return AppRouteNames.DevLogin;
      }
    },
  },
  {
    path: "/dev-login",
    name: AppRouteNames.DevLogin,
    component: DevLoginApp,
    beforeEnter: async () => {
      const userIsAuthenticated = await isAuthenticated();
      if (userIsAuthenticated) {
        return AppRouteNames.Dashboard;
      }

      if (!isDevelopment) {
        return AppRouteNames.Login;
      }
    },
  },
  {
    path: "/finish-registration",
    component: FinishRegistrationApp,
    children: [...finishRegistrationRoutes],
  },
  {
    path: "/general-error",
    name: AppRouteNames.GeneralError,
    component: GeneralErrorApp,
  },
  {
    path: "/maintenance",
    name: AppRouteNames.Maintenance,
    component: MaintenanceApp,
  },
  {
    path: "/impersonate",
    name: AppRouteNames.Impersonate,
    component: { render: () => null },
    beforeEnter: async (to: RouteLocationGeneric) => {
      const userIsAuthenticated = await isAuthenticated();
      if (!userIsAuthenticated) {
        return { name: AppRouteNames.Login };
      }

      const superUser = await isSuperUser();
      if (to.query.user_id && superUser) {
        setImpersonationCookie(to.query.user_id as string);
      }
      return { name: AppRouteNames.Dashboard, query: {} };
    },
  },
];
